import { isAxiosError } from 'axios'
import { apiErrorMessage, apiErrorStatus } from '@/lib/api-error'

const NOT_FOUND = 404
const UNPROCESSABLE = 422
const SERVICE_UNAVAILABLE = 503

const UNREACHABLE = 'ATLAS could not be reached. Check your connection and try again.'

/** A request that never got an answer; axios's own message for it ("Network Error") says nothing useful. */
const isUnreachable = (error: unknown) => isAxiosError(error) && !error.response

/** Why the imports could not be listed. */
export const listErrorMessage = (error: unknown) =>
	isUnreachable(error) ? UNREACHABLE : apiErrorMessage(error, 'The imports could not be read. Try again.')

/** Why an asset code could not be imported, phrased as what to do next. */
export function importErrorMessage(error: unknown, assetCode: string): string {
	if (isUnreachable(error)) return UNREACHABLE
	switch (apiErrorStatus(error)) {
		case UNPROCESSABLE:
			return `${assetCode} is not an asset code. Asset codes look like ASSET-12345678.`
		case NOT_FOUND:
			return `Vault has no document with asset code ${assetCode}. Check the code in Vault.`
		case SERVICE_UNAVAILABLE:
			return 'Vault did not answer, so nothing was imported. Try again in a moment.'
		default:
			// A 409 is either duplicate documents in Vault or a raced import; only the server's text tells them apart.
			return apiErrorMessage(error, `${assetCode} could not be imported from Vault.`)
	}
}

/**
 * Why a file could not be downloaded. A 404 means the list on screen is stale,
 * which the caller refreshes, so the message says so.
 */
export function downloadErrorMessage(error: unknown, label: string): string {
	if (isUnreachable(error)) return UNREACHABLE
	if (apiErrorStatus(error) === NOT_FOUND) {
		return `${label} was deleted from the library. The list has been refreshed.`
	}
	return apiErrorMessage(error, `${label} could not be downloaded.`)
}
